import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'wouter';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import Cal from '@calcom/embed-react';
import { apiRequest } from '@/lib/queryClient';
import { PageLayout } from '@/components/ui/page-layout';
import { ArrowLeft, ArrowRight, Calendar, Check } from 'lucide-react';
import { format } from 'date-fns';
import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';

export default function CalendarBookingPage() {
  const [location, navigate] = useLocation();
  const { toast } = useToast();
  const [bookingComplete, setBookingComplete] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Parse URL parameters
  const urlParams = new URLSearchParams(location.split('?')[1] || '');
  const customerId = parseInt(urlParams.get('customerId') || '0');
  const customerName = urlParams.get('customerName') || 'Customer';

  // Fetch customer details if we have an ID
  const { data: customer } = useQuery({
    queryKey: [`/api/customers/${customerId}`],
    queryFn: () => apiRequest('GET', `/api/customers/${customerId}`),
    enabled: customerId > 0,
  });

  // Update customer with booking date mutation
  const updateCustomerMutation = useMutation({
    mutationFn: (date: Date) => {
      return apiRequest('PATCH', `/api/customers/${customerId}`, {
        initialContactDate: date.toISOString(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/customers'] });
      setBookingComplete(true);

      toast({
        title: 'Calendar Updated',
        description: `Meeting with ${customerName} scheduled successfully.`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Update Failed',
        description: `Could not update customer record: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Handle successful booking from Cal.com
  const handleDateSelected = (date: Date) => {
    setSelectedDate(date);
    updateCustomerMutation.mutate(date);
  };

  // Initialize Cal.com API integration
  useEffect(() => {
    const initCalApi = async () => {
      try {
        const cal = await (window as any).Cal('init');

        // Listen for booking events
        cal('on', {
          action: 'bookingSuccessful',
          callback: (e: any) => {
            console.log('Cal.com booking successful:', e);
            if (e.detail && e.detail.data && e.detail.data.date) {
              handleDateSelected(new Date(e.detail.data.date));
            }
          },
        });
      } catch (err) {
        console.error('Error initializing Cal.com:', err);
      }
    };

    initCalApi();
  }, [handleDateSelected]);

  // Make sure we have a valid customer ID
  if (customerId === 0) {
    return (
      <div className="container mx-auto py-8">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <h2 className="text-xl font-semibold mb-2">Invalid Customer</h2>
              <p className="text-muted-foreground">No valid customer ID was provided.</p>
              <Button className="mt-4" onClick={() => (window.location.href = '/pipeline')}>
                Return to Pipeline
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <Button variant="ghost" className="mb-4" onClick={() => navigate('/pipeline')}>
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to Pipeline
      </Button>

      <h1 className="text-2xl font-bold mb-6">Schedule a Meeting</h1>

      {bookingComplete ? (
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="rounded-full bg-green-100 p-3 mb-4">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Meeting Scheduled!</h2>
              <p className="text-muted-foreground mb-2">
                Your meeting with {customerName} has been scheduled for:
              </p>
              <p className="text-lg font-medium mb-6">
                {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy h:mm a')}
              </p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => navigate('/pipeline')}>
                  Return to Pipeline
                </Button>
                <Button onClick={() => navigate(`/customers?view=${customerId}`)}>
                  <ArrowRight className="h-4 w-4 mr-1" />
                  View Customer
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Calendar className="h-5 w-5 mr-2" />
                Schedule Meeting with {customerName}
              </CardTitle>
            </CardHeader>
            <Separator />
            <CardContent className="pt-6">
              <div className="h-[650px] w-full rounded-md overflow-hidden">
                <Cal
                  calLink="mquinton34/30min"
                  style={{ width: '100%', height: '100%', overflow: 'scroll' }}
                  config={{
                    name: customerName,
                    email: customer?.email || '',
                    notes: `Customer meeting with ${customerName}${customer?.phone ? `\nPhone: ${customer.phone}` : ''}${customer?.address ? `\nAddress: ${customer.address}` : ''}`,
                    layout: 'column_view',
                  }}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
